import { useFaxInbox, useFaxTaskSummaries } from '@/hooks'
import type { FaxTaskSummary } from '@/hooks'
import { Badge, Group, Loader, NavLink } from '@mantine/core'
import { formatDateTime } from '@medplum/core'
import {
  IconAlertTriangle,
  IconCheck,
  IconClipboardList,
  IconExclamationMark,
  IconInbox,
} from '@tabler/icons-react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { NavLink as Link } from 'react-router-dom'

export function FaxInbox() {
  const { data, hasNextPage, fetchNextPage } = useFaxInbox()

  const items = data.pages.flatMap((page) => page.items)

  // Bulk lookup for the whole loaded page — never call per row, that turns a
  // paginated list into N requests.
  const { byDocumentReference } = useFaxTaskSummaries(
    items.map((i) => i.documentReferenceId).filter((id): id is string => !!id),
  )

  if (!items.length) {
    return <FaxInboxEmpty />
  }

  return (
    <InfiniteScroll
      dataLength={items.length}
      next={() => fetchNextPage()}
      hasMore={hasNextPage ?? false}
      loader={<Loader size="sm" className="mx-auto my-2" />}
      scrollableTarget="fax-inbox-scroll"
    >
      <ul role="list" className="divide-y divide-gray-200">
        {items.map((item) => {
          const taskState = item.documentReferenceId
            ? byDocumentReference.get(item.documentReferenceId)?.state
            : undefined

          return (
            <li key={item.taskId}>
              <NavLink
                component={Link}
                to={`/fax/inbox/${item.taskId}`}
                label={item.senderPhone}
                description={formatDateTime(item.receivedAt)}
                leftSection={
                  <Group gap={4} wrap="nowrap">
                    <FaxInboxItemStatus status={item.status} />
                    <FaxTaskStateBadge state={taskState} />
                  </Group>
                }
              />
            </li>
          )
        })}
      </ul>
    </InfiniteScroll>
  )
}

function FaxInboxItemStatus({ status }: { status: string }) {
  switch (status) {
    case 'completed': {
      return (
        <Badge size="xs" circle color="green">
          <IconCheck size={10} />
        </Badge>
      )
    }
    default: {
      return (
        <Badge size="xs" circle color="yellow">
          <IconExclamationMark size={10} />
        </Badge>
      )
    }
  }
}

function FaxTaskStateBadge({ state }: { state: FaxTaskSummary['state'] | undefined }) {
  switch (state) {
    case 'overdue': {
      return (
        <Badge size="xs" circle color="red" title="Has an overdue follow-up task">
          <IconAlertTriangle size={10} />
        </Badge>
      )
    }
    case 'pending': {
      return (
        <Badge size="xs" circle color="yellow" title="Has a pending follow-up task">
          <IconClipboardList size={10} />
        </Badge>
      )
    }
    default: {
      return null
    }
  }
}

function FaxInboxEmpty() {
  return (
    <div className="flex flex-auto flex-col items-center justify-center">
      <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
        <div className="flex size-16 items-center justify-center rounded-full bg-gray-100">
          <IconInbox className="size-8 text-gray-400" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900">Empty</p>
          <p className="mt-1 text-xs text-gray-500">Incoming faxes will appear here.</p>
        </div>
      </div>
    </div>
  )
}
